// mds-dojo — dojox/gesture/tap — FAITHFUL port of references/dojox/gesture/tap.js.
//
// Provides tap gesture event handlers on top of ./Base:
//   - dojox.gesture.tap            → 'tap' event
//   - dojox.gesture.tap.hold       → 'tap.hold' event
//   - dojox.gesture.tap.doubletap  → 'tap.doubletap' event
//
// A default instance is published as dojox.gesture.tap (usable with dojo.connect /
// dojo/on), and the class itself as dojox.gesture.tap.Tap so callers can create a
// configured instance (e.g. new dojox.gesture.tap.Tap({holdThreshold: 300})).
//
// PORT NOTES: AMD define(...) → ESM imports; classic declare() kept via the faithful
// dojo/_base/declare-runtime. dojo/_base/kernel dropped (only used for the
// unimplemented kernel.experimental()).

import declare from '../../dojo/_base/declare-runtime';
import lang from '../../dojo/_base/lang';
import Base from './Base';
import dojox from '../main';

// Declare an internal anonymous class which will only be exported by the module value
// e.g. dojox.gesture.tap.Tap
var clz = declare(/*===== "dojox.gesture.tap", =====*/Base, {
  // defaultEvent: [readonly] String
  //		Default event - 'tap'
  defaultEvent: 'tap',

  // subEvents: [readonly] Array
  //		List of sub events, combined with defaultEvent as 'tap.hold', 'tap.doubletap'.
  subEvents: ['hold', 'doubletap'],

  // holdThreshold: Integer
  //		Threshold (in milliseconds) for 'tap.hold'
  holdThreshold: 500,

  // doubleTapTimeout: Integer
  //		Timeout (in milliseconds) for 'tap.doubletap'
  doubleTapTimeout: 250,

  // tapRadius: Integer
  //		Valid tap radius from previous touch point
  tapRadius: 10,

  press: function (/*Object*/data, /*Event*/e) {
    // summary:
    //		Overwritten, record initial tap info and register a timeout checker for 'tap.hold'
    if (e.touches && e.touches.length >= 2) {
      // tap gesture is only for single touch
      clearTimeout(data.tapTimeOut);
      delete data.context;
      return;
    }
    var target = e.target;
    this._initTap(data, e);
    data.tapTimeOut = setTimeout(lang.hitch(this, function () {
      if (this._isTap(data, e)) {
        this.fire(target, { type: 'tap.hold' });
      }
      delete data.context;
    }), this.holdThreshold);
  },

  release: function (/*Object*/data, /*Event*/e) {
    // summary:
    //		Overwritten, fire matched 'tap' or 'tap.doubletap' during touchend
    if (!data.context) {
      clearTimeout(data.tapTimeOut);
      return;
    }
    if (this._isTap(data, e)) {
      switch (data.context.c) {
        case 1:
          this.fire(e.target, { type: 'tap' });
          break;
        case 2:
          this.fire(e.target, { type: 'tap.doubletap' });
          break;
      }
    }
    clearTimeout(data.tapTimeOut);
  },

  _initTap: function (/*Object*/data, /*Event*/e) {
    // summary:
    //		Update the gesture data with new tap info
    if (!data.context) {
      data.context = { x: 0, y: 0, t: 0, c: 0 };
    }
    var ct = new Date().getTime();
    if (ct - data.context.t <= this.doubleTapTimeout) {
      data.context.c++;
    } else {
      data.context.c = 1;
      data.context.x = e.screenX;
      data.context.y = e.screenY;
    }
    data.context.t = ct;
  },

  _isTap: function (/*Object*/data, /*Event*/e) {
    // summary:
    //		Check whether it's a valid tap
    if (!data.context) {
      return false;
    }
    var dx = Math.abs(data.context.x - e.screenX);
    var dy = Math.abs(data.context.y - e.screenY);
    return dx <= this.tapRadius && dy <= this.tapRadius;
  }
});

// the default tap instance for handy use
dojox.gesture.tap = new clz();
// Class for creating a new Tap instance
dojox.gesture.tap.Tap = clz;

export default dojox.gesture.tap;
